import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import {
  ApiService,
  BasicFood,
  CheckboxArrayOptionSimple,
  FoodKind,
  ForeignBasicFood,
  SubscriptionSink,
  checkboxTitleComparator
} from '@app/shared';
import { debounceTime, first, map, switchMap, tap } from 'rxjs/operators';
import { merge } from 'rxjs';
import { nutribaseFoodGroup } from '../../shared';

@Component({
  selector: 'app-product-list',
  templateUrl: './product-list.component.html',
  styleUrls: ['./product-list.component.scss']
})
export class ProductListComponent implements OnInit, OnDestroy {

  @Input()
  selected: number;

  @Input()
  canDelete: boolean;

  @Input()
  includeIncomplete: boolean = false;

  @Input()
  l3FoodGroups: nutribaseFoodGroup[];

  @Output()
  pick: EventEmitter<BasicFood> = new EventEmitter<BasicFood>();

  @Output()
  delete: EventEmitter<BasicFood> = new EventEmitter<BasicFood>();

  yearId: number;

  productList: (BasicFood | ForeignBasicFood)[] = [];

  searchForm: FormGroup;

  anyFilter = false;

  allL3FoodGroupsOptions: CheckboxArrayOptionSimple[];

  protected offset = 0;

  protected limit = 30;

  protected count: number;

  protected subscription = new SubscriptionSink();

  constructor(protected api: ApiService, protected fb: FormBuilder, protected router: Router) {
  }

  ngOnInit() {
    this.searchForm = this.fb.group({
      query: [],
      foodGroup: [],
    });

    this.subscription.sink = merge(
      this.searchForm
        .valueChanges
        .pipe(
          tap((v) =>
            this.anyFilter = !!v.foodGroup
          ),
        ),
      this.api.foodImport$,
    ).pipe(
      debounceTime(250),
      switchMap(() => this.fetch(0))
    ).subscribe()

    this.subscription.sink = this.fetch().subscribe();

    this.subscription.sink = this.api
      .food$
      .pipe(
        switchMap(() => this.fetch())
      )
      .subscribe();

    this.allL3FoodGroupsOptions = (this.l3FoodGroups || []).map(group => ({
      title: group.FGNM,
      value: group.id
    }));
    this.allL3FoodGroupsOptions.sort(checkboxTitleComparator);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  showProduct(food: BasicFood) {
    this.pick.emit(food);
  }

  deleteProduct(food: BasicFood) {
    this.delete.emit(food);
  }

  resetSearchForm() {
    this.searchForm.patchValue({
      foodGroup: null,
    });
  }

  clearFilter(name: string) {
    this.searchForm.get(name).setValue(null, {});
  }

  loadMore() {
    this.subscription.sink = this.fetch(this.offset + this.limit).subscribe();
  }

  protected fetch(offset?: number) {
    const query = this.searchForm.value.query;

    this.offset = offset ?? 0;
    if (this.offset === 0) {
      this.productList = [];
    }

    return this.api.getYearId().pipe(
      tap(yearId => this.yearId = yearId),
      switchMap(yearId =>
        this.api.foodFindFood({
          yearId,
          query,
          kind: FoodKind.Product,
          offset: this.offset,
          limit: this.limit,
          foodGroupId: this.searchForm.value.foodGroup,
          includeIncomplete: this.includeIncomplete
        })
      ),
      map(response => {
        let newFoods = 0;
        response.foods.forEach(f => {
          if (!this.productList.find(p => p.id === f.id)) {
            newFoods++;
            this.productList.push(f);
          }
        });
        this.count = response.count;
        return newFoods;
      }),
      first(),
    );

  }
}
